import { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from '../../../i18n/translations';

type Category = 'cabling' | 'datacenter' | 'voip';

interface GalleryImage {
  url: string;
  caption: string;
}

interface CategoryData {
  key: Category;
  label: string;
  icon: string;
  description: string;
  images: GalleryImage[];
}

interface LightboxProps {
  images: GalleryImage[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function Lightbox({ images, currentIndex, onClose, onPrev, onNext }: LightboxProps) {
  const current = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
        onClick={onClose}
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Counter */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Prev Button */}
      <button
        className="absolute left-5 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>

      {/* Image */}
      <div
        className="max-w-5xl w-full mx-20 flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full rounded-xl overflow-hidden shadow-2xl">
          <img
            src={current.url}
            alt={current.caption}
            className="w-full h-auto max-h-[70vh] object-contain bg-black"
          />
        </div>
        <div className="mt-4 text-white/80 text-sm font-medium text-center">{current.caption}</div>

        {/* Thumbnail Strip */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-1 max-w-full">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => {}}
              className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${
                idx === currentIndex ? 'border-[#00A3FF] opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Next Button */}
      <button
        className="absolute right-5 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>
    </div>
  );
}

export default function Showcase() {
  const [activeCategory, setActiveCategory] = useState<Category>('cabling');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { language } = useLanguage();
  const t = translations[language].showcase;

  const categories: CategoryData[] = [
    {
      key: 'cabling',
      label: language === 'zh' ? '結構化佈線' : 'Structured Cabling',
      icon: 'ri-git-branch-line',
      description: language === 'zh' 
        ? '符合國際標準的結構化佈線工程，整齊有序的線纜管理，為企業網絡提供穩定可靠的物理基礎。'
        : 'International standard structured cabling projects with organized cable management, providing stable and reliable physical foundation for enterprise networks.',
      images: [
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/1adf2f7ebf43ab459254c0aaa3db2fa1.jpeg',
          caption: language === 'zh' ? '機架式配線架整齊佈線' : 'Rack-mounted patch panel organized cabling',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/4fc2220e9c743030619bf3efb67e7148.jpeg',
          caption: language === 'zh' ? '配線架端口管理' : 'Patch panel port management',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/aa77f9f1f82108b4b5d745d450429b5c.jpeg',
          caption: language === 'zh' ? '天花板線槽佈線工程' : 'Ceiling cable tray installation',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/817f1e53904b80a554a84d10d0d11dc3.jpeg',
          caption: language === 'zh' ? '全機架網絡設備整合' : 'Full rack network equipment integration',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/294a3d51cc005ebe322a51e2466f0787.jpeg',
          caption: language === 'zh' ? '終端處理' : 'Terminal processing',
        },
        {
          url: 'https://public.readdy.ai/ai/img_res/95cb674223d799c2f2efd51140c40fba.jpg',
          caption: language === 'zh' ? '地台出線盒安裝完成' : 'Floor outlet box installation completed',
        },
      ],
    },
    {
      key: 'datacenter',
      label: language === 'zh' ? '數據中心機房' : 'Data Center',
      icon: 'ri-server-2-line',
      description: language === 'zh'
        ? '專業數據中心機房規劃與建置，從冷卻系統、電力配置到安全監控，打造高可用性的企業核心基礎設施。'
        : 'Professional data center planning and construction, from cooling systems and power distribution to security monitoring, building highly available enterprise core infrastructure.',
      images: [
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/3616d2aeb39dc0aba8e4bed12c6eda9f.jpeg',
          caption: language === 'zh' ? '企業級數據中心機房全景' : 'Enterprise data center panoramic view',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/946ece9d1447705a124420d7ac28681d.jpeg',
          caption: language === 'zh' ? '刀片式伺服器機架安裝' : 'Blade server rack installation',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/2e6d5e4ad0fc75d13241765caabd36cf.jpeg',
          caption: language === 'zh' ? 'UPS 不斷電系統配置' : 'UPS uninterruptible power system configuration',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/eb7b150003f3dfd8f8630d16879262de.jpeg',
          caption: language === 'zh' ? '精密空調冷卻系統' : 'Precision air conditioning cooling system',
        },
        {
          url: 'https://readdy.ai/api/search-image?query=data%20center%20monitoring%20system%20NOC%20network%20operations%20center%20multiple%20screens%20server%20status%20dashboard%20professional%20IT%20management&width=800&height=600&seq=dc05&orientation=landscape',
          caption: language === 'zh' ? '網絡運營中心監控系統' : 'Network operations center monitoring system',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/91eb85f18cc920e715af169d49063127.jpeg',
          caption: language === 'zh' ? '機袈服務器線纜管理' : 'Server rack cable management',
        },
      ],
    },
    {
      key: 'voip',
      label: language === 'zh' ? '企業級 VoIP' : 'Enterprise VoIP',
      icon: 'ri-phone-line',
      description: language === 'zh'
        ? '全面的企業 VoIP 通訊解決方案，整合 IP PBX、SIP 話機及軟電話系統，大幅提升企業通訊效率與靈活性。'
        : 'Comprehensive enterprise VoIP communication solutions, integrating IP PBX, SIP phones and softphone systems to significantly improve enterprise communication efficiency and flexibility.',
      images: [
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/bd3b6ab689dd635e50365b1857216c20.jpeg',
          caption: language === 'zh' ? '企業級 IP 桌面話機部署' : 'Enterprise IP desk phone deployment',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/7cfa5f08254626b39508f90dd3735aa1.jpeg',
          caption: language === 'zh' ? 'SIP 桌面電話機' : 'SIP desk phone',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/47fd1fec7f8b944d4c6e38df3014de47.png',
          caption: language === 'zh' ? '會議室視訊通訊系統' : 'Conference room video communication system',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/ecd5883d61f2a881acab5b948da0c7e9.png',
          caption: language === 'zh' ? 'SIP 企業會議電話機' : 'SIP enterprise conference phone',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/8f160ea2677ea1719fba557fa413f3c2.png',
          caption: language === 'zh' ? 'IP PBX 主機機架安裝' : 'IP PBX server rack installation',
        },
        {
          url: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/546c45d684641a8ee56f4f378a57dfe9.jpeg',
          caption: language === 'zh' ? 'SIP 門鈴對講機' : 'SIP door intercom',
        },
      ],
    },
  ];

  const currentCategory = categories.find((c) => c.key === activeCategory)!;
  const images = currentCategory.images;

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () =>
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + images.length) % images.length : 0));
  const nextImage = () =>
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % images.length : 0));

  return (
    <section id="showcase" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-block px-4 py-1.5 bg-gray-100 text-gray-600 text-xs font-semibold tracking-wider rounded-full mb-4 uppercase">
            Showcase
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {language === 'zh' ? '案例展示' : 'Project Showcase'}
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            {language === 'zh'
              ? '真實工程案例呈現，每一個項目均代表我們對品質與專業的承諾。'
              : 'Real project cases showcasing our commitment to quality and professionalism in every project.'}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.key
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400 hover:text-gray-900'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Category Description */}
        <div className="text-center mb-8">
          <p className="text-sm text-gray-500 max-w-xl mx-auto">{currentCategory.description}</p>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-3 gap-4">
          {images.map((img, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-xl cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300"
              style={{ aspectRatio: '4/3' }}
              onClick={() => openLightbox(index)}
            >
              <img
                src={img.url}
                alt={img.caption}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium">{img.caption}</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                    <i className="ri-zoom-in-line text-white text-base"></i>
                  </div>
                </div>
              </div>
              {/* Index Badge */}
              <div className="absolute top-3 left-3 w-7 h-7 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-full text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {index + 1}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 mb-4">
            {language === 'zh'
              ? '對我們的工程質素感興趣？歡迎聯絡我們了解更多案例詳情。'
              : 'Interested in our engineering quality? Contact us to learn more about our project details.'}
          </p>
          <a
            href="#contact"
            className="inline-flex items-center space-x-2 px-8 py-3.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>{language === 'zh' ? '查詢工程案例' : 'Inquire About Projects'}</span>
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </section>
  );
}